import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import { SearchBoxLocal } from "@covestro/mui-theme";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { Box, Stack, Toolbar, Typography } from "@mui/material";
import type { NamedColor } from "components/filter/color-filter/ColorFilter";
import { ZoomSlider } from "components/search-results/grid/ZoomSlider";
import {
  type SearchResultsLayout,
  SearchResultsLayoutInput,
} from "components/search-results/SearchResultsLayoutInput";
import type { SortEntry } from "hooks/sort";
import { SearchSort } from "pages/search/SearchSort";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MatchedMaterialsGrid } from "./materials/MatchedMaterialsGrid";
import { useResults } from "./ResultsContext";

interface MatchedMaterialsPanelProps {
  coloredMaterials?: { results: ColoredMaterial[]; totalCount: number };
  isLoading: boolean;
  error?: Error;
  sort: SortEntry[];
  onSortChange: (sort: SortEntry[]) => void;
  onFilterReset?: () => void;
  regionLabel?: string;
  /** Hex color used to tint the region badge background (applied at 15% opacity). */
  regionColor?: string;
}

/** Panel rendering matched materials for a selected region's color palette entry,
 * with search-within-results, sort, zoom, and grid/list view-toggle controls. */
export const MatchedMaterialsPanel = ({
  coloredMaterials,
  isLoading,
  error,
  sort,
  onSortChange,
  onFilterReset,
  regionLabel,
  regionColor,
}: MatchedMaterialsPanelProps) => {
  const { t } = useTranslation();
  const { searchQuery, setSearchQuery, viewMode, setViewMode, selectedColor } =
    useResults();
  const [gridSize, setGridSize] = useState(2);

  const target: NamedColor | undefined = selectedColor
    ? { value: selectedColor.cieLab, name: selectedColor.hex }
    : undefined;

  // The shared layout toggle speaks "grid" | "table"; the results view-state
  // (and MatchedMaterialsGrid) speak "grid" | "list". Bridge the two so the
  // Upload-in-Match toggle matches CIELAB search exactly (table-first order,
  // same icons) without changing the view-state vocabulary.
  const layout: SearchResultsLayout = viewMode === "list" ? "table" : "grid";
  const handleLayoutChange = (next: SearchResultsLayout) =>
    setViewMode(next === "table" ? "list" : "grid");

  const filteredMaterials = useMemo(() => {
    if (!coloredMaterials?.results || !searchQuery.trim()) {
      return coloredMaterials?.results;
    }
    const q = searchQuery.trim().toLowerCase();
    return coloredMaterials.results.filter(
      (m) =>
        m.material.publicGradeName?.toLowerCase().includes(q) ||
        m.color.finalColorCode?.toLowerCase().includes(q),
    );
  }, [coloredMaterials?.results, searchQuery]);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Toolbar — mirrors CIE-Lab SearchPage toolbar structure */}
      <Toolbar
        disableGutters
        sx={{
          flexWrap: { xs: "wrap", md: "nowrap" },
          rowGap: 1,
          columnGap: { xs: 1, md: 0 },
          alignItems: "center",
        }}
      >
        {/* Results title + region badge + count */}
        <Typography
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexBasis: { xs: "100%", md: "auto" },
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: 'var(--font-family, "AktivGrotesk")',
              fontWeight: 500,
              fontSize: "var(--title-t2-18-regular-size, 18px)",
              mr: 1,
            }}
          >
            {t("src_results", "Results")}
          </Box>
          {regionLabel && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "2px 8px",
                backgroundColor: regionColor
                  ? `${regionColor}15`
                  : "transparent",
                borderRadius: "9999px",
              }}
            >
              {regionColor && (
                <FiberManualRecordIcon
                  sx={{ fontSize: 6, color: regionColor }}
                />
              )}
              <Typography
                component="span"
                sx={{
                  fontSize: "var(--footnote-f1-10-regular-size, 10px)",
                  color: regionColor || "#262626",
                }}
              >
                {regionLabel}
              </Typography>
            </Box>
          )}
          {coloredMaterials && coloredMaterials.totalCount > 0 && (
            <Typography
              component="span"
              color="text.secondary"
              fontSize="var(--body-b3-12-regular-size, 12px)"
            >
              {t("ldp_explore_image_results_count", {
                count: coloredMaterials.totalCount,
                defaultValue: "{{count}} results",
              })}
            </Typography>
          )}
        </Typography>

        {/* Search within results — hidden only once a completed fetch reports
            zero results (AC#3): "search within results" makes no sense with an
            empty result set. Kept mounted while loading, because the request
            hook resets its result to `undefined` mid-refetch (useRequest) — so
            gating on `totalCount > 0` alone would unmount/remount the box on
            every pagination/sort/filter change, not just the genuine 0-result
            state. */}
        {(isLoading ||
          (coloredMaterials && coloredMaterials.totalCount > 0)) && (
          <Box
            sx={{
              flexGrow: 1,
              mr: { xs: 0, md: 4 },
              display: "flex",
              alignItems: "center",
              flexBasis: { xs: "100%", md: "auto" },
            }}
          >
            <SearchBoxLocal
              onSearch={setSearchQuery}
              placeHolderText={
                (coloredMaterials?.totalCount ?? 0) > 500 || isLoading
                  ? t("src_search_within_result_out")
                  : t("src_search_within_result_in")
              }
              width="100%"
              total={coloredMaterials?.totalCount}
              loading={isLoading}
              query={searchQuery}
              setQuery={setSearchQuery}
            />
          </Box>
        )}

        {/* Sort */}
        <Box
          sx={{
            flexGrow: 0,
            mr: { xs: 1, md: 4 },
            display: "flex",
            alignItems: "center",
          }}
        >
          <SearchSort
            query={searchQuery}
            value={sort}
            onChange={onSortChange}
          />
        </Box>

        {/* Zoom slider (grid only) */}
        {viewMode === "grid" && (
          <Box
            sx={{
              flexGrow: 0,
              mr: { xs: 1, md: 4 },
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
            }}
          >
            <ZoomSlider
              value={gridSize}
              onChange={setGridSize}
              min={1}
              max={3}
            />
          </Box>
        )}

        {/* View toggle */}
        <SearchResultsLayoutInput
          value={layout}
          onChange={handleLayoutChange}
        />
      </Toolbar>

      {/* Results */}
      <Stack rowGap={2} alignItems="center">
        {!error && (
          <MatchedMaterialsGrid
            values={filteredMaterials}
            loading={isLoading}
            viewMode={viewMode}
            gridSize={gridSize}
            sort={sort}
            onSortChange={onSortChange}
            onFilterReset={onFilterReset}
            target={target}
          />
        )}
        {error && (
          <Typography
            color="error"
            fontSize="var(--body-b2-14-regular-size, 14px)"
          >
            {error.message}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};
